import { createFileRoute, getRouteApi, useRouter } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { settingsService } from '@/services/settings';
import { useToast } from '@/hooks/useToast';
import { showFlashMessage } from '@/utils/flashMessageUtils';
import ProfileSettings from '@/components/Settings/ProfileSettings';

const settingsRoute = getRouteApi('/_app/settings');

export const Route = createFileRoute('/_app/settings/profile')({
  component: ProfileSettingsPage,
});

function ProfileSettingsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const settings = settingsRoute.useLoaderData();
  const { showToast } = useToast();
  const formKey = settings.user.formKey;

  // Avatar upload mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: ({ file, formKey }: { file: File; formKey: string }) =>
      settingsService.uploadAvatar(file, formKey),
    onSuccess: async () => {
      showFlashMessage(showToast);
      await router.invalidate();
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Display name mutation
  const updateDisplayNameMutation = useMutation({
    mutationFn: ({ displayName, formKey }: { displayName: string; formKey: string }) =>
      settingsService.updateDisplayName(displayName, formKey),
    onSuccess: async () => {
      showFlashMessage(showToast);
      await router.invalidate();
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Username mutation
  const updateUsernameMutation = useMutation({
    mutationFn: ({ username, formKey }: { username: string; formKey: string }) =>
      settingsService.updateUsername(username, formKey),
    onSuccess: async () => {
      showFlashMessage(showToast);
      await router.invalidate();
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Privacy toggle mutation
  const togglePrivacyMutation = useMutation({
    mutationFn: (formKey: string) => settingsService.togglePrivacy(formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  return (
    <ProfileSettings
      profile={settings.profile}
      formKey={formKey}
      onAvatarUpload={async (file: File) => {
        return await uploadAvatarMutation.mutateAsync({ file, formKey });
      }}
      isUploadingAvatar={uploadAvatarMutation.isPending}
      onDisplayNameUpdate={(newDisplayName: string) => {
        updateDisplayNameMutation.mutate({
          displayName: newDisplayName,
          formKey,
        });
      }}
      onUsernameUpdate={(newUsername: string) => {
        updateUsernameMutation.mutate({
          username: newUsername,
          formKey,
        });
      }}
      onPrivacyToggle={() => {
        togglePrivacyMutation.mutate(formKey);
      }}
      isUpdatingDisplayName={updateDisplayNameMutation.isPending}
      isUpdatingUsername={updateUsernameMutation.isPending}
      isTogglingPrivacy={togglePrivacyMutation.isPending}
    />
  );
}
